import React from 'react';
import { useNotNullableContext } from '../../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../../context/PageMetaContext';
import {
  closeIcon,
  emailIcon,
  facebookIcon,
  twitterIcon,
  whatsappIcon,
} from '@justgiving/icons';
import Svg from '@justgiving/svg';
import styles from './ShareModal.scss';
import classNames from 'classnames';
import isMobile from '../../../utils/isMobile';
import { usePageOwner } from '../../../hooks/usePageOwner';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../../hooks/usePageQuery';
import gql from 'graphql-tag';
import { ShareModalFragment } from '../../../../types/__generated__';
import { useTranslation } from 'react-i18next';
import IconButton from '../../IconButton/IconButton';
import generateHash from '../../../utils/generateHash';
import { filterByTypename } from '../../../utils/graphql';
import { generateUrlWithShareParameter } from '../../../utils/generateUrlWithShareParameter';
import { ShareQueryParameter } from '../../../constants/social';

const FRAGMENT = buildQuery(gql`
  fragment ShareModal on Page {
    title
    owner {
      name
    }
    relationships {
      beneficiaries(first: 1) {
        totalCount
        nodes {
          ... on Charity {
            name
          }
        }
      }
    }
  }
`);

interface ShareModalProps {
  onClose: () => void;
  imageUrl?: string;
  description?: string | null;
}

const ShareModal = React.forwardRef(
  (
    { onClose, imageUrl, description }: ShareModalProps,
    ref?: React.Ref<HTMLDivElement>,
  ) => {
    const { t } = useTranslation('common', { useSuspense: false });
    const result = usePageQuery<ShareModalFragment>(FRAGMENT);
    const { currentUrl, pageType, pageShortName } = useNotNullableContext(
      PageMetaContext,
    );
    const { isPageOwner } = usePageOwner();

    if (!UsePageQueryResult.hasData(result) || !result.data) {
      return null;
    }

    const { title, owner } = result.data;
    const charities = filterByTypename(
      result.data.relationships?.beneficiaries?.nodes ?? [],
      'Charity',
    );
    const metaTagValues: ShareQueryParameter = {
      ogImageUrl: imageUrl?.split('?')[0],
      ogDescription: description || undefined,
    };
    const charityName = charities[0]?.name
      ? charities[0].name
      : t('charity.defaultName');

    const shareUrl = generateUrlWithShareParameter(
      currentUrl,
      metaTagValues,
      true,
    );

    const emailUrl = `${shareUrl}utm_source=Sharethis&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-email&utm_term=${generateHash()}`;
    let emailMessage: string;
    if (isPageOwner) {
      description
        ? (emailMessage = `${t('fundraising.shareUpdateEmailPageOwner', {
            charityName,
          })}${emailUrl}\n\n"${description}"`)
        : (emailMessage = t('fundraising.shareEmailPageOwner', {
            charityName,
            emailUrl,
          }));
    } else {
      description
        ? (emailMessage = `${t('fundraising.shareUpdateEmailNonPageOwner', {
            name: owner?.name,
            charityName,
          })}${emailUrl}\n\n"${description}"`)
        : (emailMessage = t('fundraising.shareEmailNonPageOwner', {
            name: owner?.name,
            charityName,
            emailUrl,
          }));
    }

    let whatsappMessage: string;
    isPageOwner
      ? (whatsappMessage = t('fundraising.shareWhatsappSMSPageOwner', {
          charityName,
        }))
      : (whatsappMessage = t('fundraising.shareWhatsappSMSNonPageOwner', {
          name: owner?.name,
          charityName,
        }));

    function handleShareOnFacebook() {
      FB.ui(
        {
          method: 'share',
          href: `${shareUrl}utm_source=facebook&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-share&utm_term=${generateHash()}`,
        },
        () => {},
      );
    }

    function handleShareOnTwitter() {
      let message: string;
      isPageOwner
        ? (message = t('fundraising.shareTwitterPageOwner', {
            charityName,
          }))
        : (message = t('fundraising.shareTwitterNonPageOwner', {
            name: owner?.name,
            charityName,
          }));
      const urlWithTracking = encodeURIComponent(
        `${shareUrl}utm_source=Twitter&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-tweet&utm_term=${generateHash()}`,
      );
      const href = `https://twitter.com/intent/tweet?url=${urlWithTracking}&text=${encodeURIComponent(
        message,
      )}`;
      window.open(href, 'newwindow', 'width=600,height=300');
    }

    return (
      <div
        aria-label={t('timeline.shareModal')}
        className={styles.content}
        ref={ref}
      >
        <IconButton
          className={styles.close}
          onClick={onClose}
          title={t('timeline.closeShareModal')}
        >
          <Svg markup={closeIcon} />
        </IconButton>
        <h1 className={styles.heading}>{t('timeline.shareModalTitle')}</h1>
        <p>{t('timeline.shareModalMessage')}</p>
        <ul className={styles.list}>
          {isMobile(window) && (
            <li>
              <IconButton className={styles.listItem} tabIndex={-1}>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `${whatsappMessage} ${shareUrl}utm_source=whatsapp&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-whatsapp&utm_term=${generateHash()}`,
                  )}`}
                  data-qa="whatsapp"
                >
                  <div
                    className={classNames(styles.iconWrapper, styles.whatsapp)}
                  >
                    <Svg markup={whatsappIcon} className={styles.icon} />
                  </div>
                  WhatsApp
                </a>
              </IconButton>
            </li>
          )}
          <li onClick={handleShareOnFacebook}>
            <IconButton className={styles.listItem}>
              <div className={classNames(styles.iconWrapper, styles.facebook)}>
                <Svg markup={facebookIcon} className={styles.icon} />
              </div>
              Facebook
            </IconButton>
          </li>
          <li onClick={handleShareOnTwitter}>
            <IconButton className={styles.listItem}>
              <div className={classNames(styles.iconWrapper, styles.twitter)}>
                <Svg markup={twitterIcon} className={styles.icon} />
              </div>
              Twitter
            </IconButton>
          </li>
          <li>
            <IconButton className={styles.listItem} tabIndex={-1}>
              <a
                href={`mailto:?subject=${encodeURIComponent(
                  title,
                )}&body=${encodeURIComponent(
                  `${t('common.greeting')},\n\n${emailMessage}\n\n${t(
                    'common.thankYou',
                  )},\n\n${isPageOwner ? owner?.name : ''}`,
                )}`}
                data-qa="email"
              >
                <div className={classNames(styles.iconWrapper, styles.email)}>
                  <Svg markup={emailIcon} className={styles.icon} />
                </div>
                {t('common.email')}
              </a>
            </IconButton>
          </li>
        </ul>
      </div>
    );
  },
);

export default ShareModal;
